import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Kanban, LayoutGrid, Plus, Table2 } from 'lucide-react'
import { useOutletContext } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { PurchaseOutletType } from '..'
import CreateOrderModal from './CreateOrderModal'
import BoardView from './BoardView'
import KanbanView from './KanbanView'
import StatusActionsMenu from './StatusActionsMenu'
import { STATUS_LABELS } from './constants'
import { PurchaseOrder, PurchaseOrderStatus } from './types'
import { buildPurchaseOrderColumns, purchaseOrderScreenKey } from './purchaseOrderColumns'
import {
  EmptyState,
  NewOrderButton,
  ScrollArea,
  StatusFilter,
  TableWrapper,
  TopBar,
  ViewToggleButton,
  ViewToggleGroup,
  Wrapper,
} from './style'

type ViewMode = 'table' | 'board' | 'kanban'

const PurchaseOrderTable = () => {
  const { refresh, setRefresh, search } = useOutletContext<PurchaseOutletType>()
  const { setFeedback } = useContext(FeedbackContext)
  const [orders, setOrders] = useState<PurchaseOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [view, setView] = useState<ViewMode>('table')

  const columns = useMemo(() => buildPurchaseOrderColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    purchaseOrderScreenKey,
    columns,
  )

  const filteredOrders = useMemo(() => {
    if (!search || !search.trim()) return orders
    const s = search.toLowerCase()
    return orders.filter((o) =>
      String(o.code || o.id).toLowerCase().includes(s) ||
      o.title?.toLowerCase().includes(s) ||
      o.description?.toLowerCase().includes(s) ||
      o.SupplierCompany?.companyName?.toLowerCase().includes(s) ||
      STATUS_LABELS[o.status]?.toLowerCase().includes(s) ||
      (o as any).PurchaseOrderItem?.some((item: any) =>
        item.PurchaseMaterial?.name?.toLowerCase().includes(s) ||
        item.PurchaseMaterial?.code?.toLowerCase().includes(s)
      )
    )
  }, [orders, search])

  const loadOrders = async () => {
    setLoading(true)
    try {
      const res = await ApiService.purchaseOrder.findAll(statusFilter ? { status: statusFilter } : undefined)
      setOrders(res.data?.data ?? res.data ?? [])
    } catch (err) {
      console.error('Erro ao carregar pedidos de compra', err)
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
  }, [refresh, statusFilter])

  const handleStatusChange = async (orderId: number, status: PurchaseOrderStatus) => {
    const previousOrders = orders
    setOrders((current) => current.map((order) => (order.id === orderId ? { ...order, status } : order)))
    try {
      await ApiService.purchaseOrder.updateStatus(orderId, status)
      setFeedback({ message: 'Status atualizado com sucesso.', isOpen: true, statusOk: true })
      // O recebimento movimenta estoque: recarrega para refletir o estado real.
      if (status === 'RECEBIDO') setRefresh((r) => !r)
    } catch (err: any) {
      console.error('Erro ao atualizar status da ordem de compra', err)
      setOrders(previousOrders)
      setFeedback({
        message: err?.response?.data?.message || 'Erro ao atualizar o status da ordem de compra.',
        isOpen: true,
        statusOk: false,
      })
    }
  }

  return (
    <Wrapper>
      <TopBar>
        <ViewToggleGroup>
          <ViewToggleButton $isActive={view === 'table'} onClick={() => setView('table')}>
            <Table2 size={14} /> Tabela
          </ViewToggleButton>
          <ViewToggleButton $isActive={view === 'board'} onClick={() => setView('board')}>
            <LayoutGrid size={14} /> Quadro
          </ViewToggleButton>
          <ViewToggleButton
            $isActive={view === 'kanban'}
            onClick={() => {
              setView('kanban')
              setStatusFilter('')
            }}
          >
            <Kanban size={14} /> Funil
          </ViewToggleButton>
        </ViewToggleGroup>

        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          {view !== 'kanban' && (
            <StatusFilter value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value=''>Todos os status</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </StatusFilter>
          )}
          <NewOrderButton onClick={() => setShowCreateModal(true)}>
            <Plus size={14} /> Nova Ordem de Compra
          </NewOrderButton>
        </div>
      </TopBar>

      {view === 'table' ? (
        <TableWrapper>
          <StandardDataTable
            columns={columns}
            data={filteredOrders}
            columnsState={columnsState}
            onColumnsChange={handleColumnsChange}
            onRestoreColumns={handleRestoreColumns}
            isLoading={loading}
            renderActions={(order) => (
              <StatusActionsMenu order={order} onStatusChange={handleStatusChange} />
            )}
            countLabel='ordens de compra'
            exportFileName='ordens-de-compra'
            exportSheetName='Ordens de Compra'
            emptyMessage='Nenhuma ordem de compra encontrada.'
          />
        </TableWrapper>
      ) : (
        <ScrollArea>
          {!loading && filteredOrders.length === 0 && <EmptyState>Nenhuma ordem de compra encontrada.</EmptyState>}

          {filteredOrders.length > 0 && view === 'board' && (
            <BoardView orders={filteredOrders} onStatusChange={handleStatusChange} />
          )}

          {filteredOrders.length > 0 && view === 'kanban' && (
            <KanbanView orders={filteredOrders} onStatusChange={handleStatusChange} />
          )}
        </ScrollArea>
      )}

      {showCreateModal && (
        <CreateOrderModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => setRefresh((r) => !r)}
        />
      )}
    </Wrapper>
  )
}

export default PurchaseOrderTable
